/**
 * Choosing a place, and what happens to it afterwards.
 *
 * The old shape asked the question backwards. "Pin this spot" committed you to *here* before you
 * had chosen anywhere, made you name it before you knew what it was, and then offered a map at
 * the bottom of the screen that quietly broke both promises — and if you took it, the picker
 * asked for the name a second time. Meanwhile the same act, with a different fate, lived on a
 * second screen ("Send someone here") that did it all over again.
 *
 * There is one act here now, in the order the act actually happens:
 *
 *     choose the point  →  say what it is  →  decide what it is for
 *
 * "Here" is not a separate flow, it is where the crosshair starts and a one-tap button to get
 * back to. The fate — keep it, send it, walk to it now — is asked last, because that is the one
 * question you can genuinely answer before standing anywhere.
 */

import {
  buildArrivalLink,
  describeAge,
  PIN_SUGGESTIONS,
  type Destination,
} from '../lib/destination';
import { el } from '../lib/dom';
import { navigate } from '../lib/navigate';
import { getPin, listPins, removePin, savePin, type SavedPin } from '../lib/pins';
import type { View } from '../lib/router';
import { canNativeShare, copyText, shareLink } from '../lib/share';
import { errorScreen, toast } from '../ui/feedback';
import { markLogo } from '../ui/mark';
import { placePicker, type PlacePicker } from './place';
import { arrivedScreen, runSolo } from './solo';

/** What the caller came here to do, if they said. */
type Intent = 'pin' | 'link' | null;

function requestedIntent(): Intent {
  const asked = new URLSearchParams(window.location.search).get('for');
  if (asked === 'pin' || asked === 'link') return asked;
  // `/go/new` was the old door to "make an arrival link". Links to it exist in the wild, so it
  // keeps its meaning rather than dropping people on a screen that asks a question again.
  return window.location.pathname.startsWith('/go/') ? 'link' : null;
}

/* -------------------------------------------------------------------------- */
/* /place — the one picker                                                    */
/* -------------------------------------------------------------------------- */

export const placeView: View = (container) => {
  const intent = requestedIntent();

  let picker: PlacePicker | null = null;
  let walking: (() => void) | null = null;

  const clear = (): void => {
    picker?.destroy();
    picker = null;
    walking?.();
    walking = null;
  };

  const show = (node: HTMLElement): void => {
    clear();
    container.replaceChildren(node);
  };

  /** Walk to a place that was just chosen but never saved. Nothing is written anywhere. */
  const walkTo = (destination: Destination): void => {
    clear();
    walking = runSolo(container, 'pin', destination, {
      onArrived: () =>
        arrivedScreen({
          title: `You are at ${destination.label}`,
          body: 'Nothing about this trip was recorded.',
          onDone: () => navigate('/'),
        }),
    });
  };

  /* --- the link ---------------------------------------------------------- */

  const linkScreen = (destination: Destination): HTMLElement => {
    const link = buildArrivalLink(destination, window.location.origin);
    const actions = el('div', { class: 'screen__actions' });

    const send = el(
      'button',
      { class: 'button button--primary button--block', type: 'button', 'data-action': 'send-link' },
      canNativeShare() ? 'Send the link' : 'Copy the link',
    );
    send.addEventListener('click', () => {
      void shareLink(link, 'Here is where to find me.').then((outcome) => {
        if (outcome === 'copied') toast(actions, 'Link copied');
        else if (outcome === 'failed') void copyText(link);
      });
    });

    actions.append(
      send,
      el(
        'button',
        {
          class: 'button button--ghost button--block',
          type: 'button',
          onclick: () => show(fateScreen(destination)),
        },
        'Do something else with it',
      ),
      el(
        'button',
        {
          class: 'button button--ghost button--block',
          type: 'button',
          onclick: () => navigate('/'),
        },
        'Done',
      ),
    );

    return el(
      'main',
      { class: 'screen screen--centered solo' },
      el('h1', { class: 'screen__title', text: `A link to ${destination.label}` }),
      el('p', {
        class: 'screen__body',
        text: 'They open it and get an arrow. No app, no account, nothing to install.',
      }),
      el('p', { class: 'share__url mono', text: link }),
      actions,
      el('p', {
        class: 'privacy',
        text: 'The destination is after the # in that link, so it is never sent to Beeline. Only whoever you send it to can see it.',
      }),
    );
  };

  /* --- saved ------------------------------------------------------------- */

  const savedScreen = (pin: SavedPin): HTMLElement =>
    el(
      'main',
      { class: 'screen screen--centered solo' },
      el('div', { class: 'converged__mark', 'aria-hidden': 'true' }),
      el('h1', { class: 'screen__title', text: `${pin.label} is saved` }),
      pin.photo ? el('img', { class: 'solo__photo', src: pin.photo, alt: '' }) : null,
      el('p', {
        class: 'screen__body',
        text: 'It stays on this phone. Come back to it any time from the home screen.',
      }),
      el(
        'div',
        { class: 'screen__actions' },
        el(
          'button',
          {
            class: 'button button--primary button--block',
            type: 'button',
            onclick: () => navigate(`/pin/${pin.id}`),
          },
          'Take me there now',
        ),
        el(
          'button',
          {
            class: 'button button--ghost button--block',
            type: 'button',
            onclick: () => show(linkScreen(pin)),
          },
          'Send it to somebody',
        ),
        el(
          'button',
          {
            class: 'button button--ghost button--block',
            type: 'button',
            onclick: () => navigate('/'),
          },
          'Done',
        ),
      ),
    );

  /* --- what is it for? --------------------------------------------------- */

  /**
   * The last question, and the only one that could not have been asked earlier.
   *
   * When the caller already said what they came for, that fate is the primary button and the
   * others are still one tap away — which is what makes "pin this spot" and "send someone here"
   * stop being two flows that cannot change their mind.
   */
  const fateScreen = (destination: Destination): HTMLElement => {
    const keep = el(
      'button',
      {
        class: `button button--block ${intent === 'link' ? 'button--ghost' : 'button--primary'}`,
        type: 'button',
        'data-action': 'keep',
      },
      'Save it on this phone',
    );
    keep.addEventListener('click', () => show(savedScreen(savePin(destination))));

    const send = el(
      'button',
      {
        class: `button button--block ${intent === 'link' ? 'button--primary' : 'button--ghost'}`,
        type: 'button',
        'data-action': 'send',
      },
      'Send it to somebody',
    );
    send.addEventListener('click', () => show(linkScreen(destination)));

    const walk = el(
      'button',
      { class: 'button button--ghost button--block', type: 'button', 'data-action': 'walk' },
      'Just walk me there now',
    );
    walk.addEventListener('click', () => walkTo(destination));

    const again = el(
      'button',
      { class: 'button button--ghost button--block', type: 'button' },
      'Pick a different spot',
    );
    again.addEventListener('click', () => openPicker(destination));

    return el(
      'main',
      { class: 'screen screen--centered solo' },
      el('h1', { class: 'screen__title', text: destination.label }),
      destination.photo
        ? el('img', { class: 'solo__photo', src: destination.photo, alt: '' })
        : null,
      el('p', { class: 'screen__body', text: 'What is it for?' }),
      el(
        'div',
        { class: 'screen__actions' },
        ...(intent === 'link' ? [send, keep] : [keep, send]),
        walk,
        again,
      ),
      el('p', {
        class: 'privacy',
        text: 'Saved places and links both stay off our server. Nothing here is sent anywhere you did not send it.',
      }),
    );
  };

  /* --- the map ----------------------------------------------------------- */

  const openPicker = (previous: Destination | null): void => {
    clear();

    const chooser = placePicker({
      title:
        intent === 'link' ? 'Send someone a place' : intent === 'pin' ? 'Save a place' : 'A place',
      body: 'Drag the map so the crosshair is on it. It can be right where you are standing.',
      confirmLabel: 'That is the spot',
      hereLabel: 'That is where I am',
      suggestions: PIN_SUGGESTIONS,
      defaultLabel: intent === 'link' ? 'Destination' : 'Pinned spot',
      // A photo cannot ride in a URL fragment, and the fragment is the only place an arrival
      // link is allowed to keep anything.
      allowPhoto: intent !== 'link',
      start: previous ? { lat: previous.lat, lng: previous.lng } : null,
      onConfirm: (destination) => show(fateScreen(destination)),
      onCancel: () => navigate('/'),
    });

    picker = chooser;
    container.replaceChildren(chooser.element);
  };

  openPicker(null);

  return () => clear();
};

/* -------------------------------------------------------------------------- */
/* /pin — the places already saved                                            */
/* -------------------------------------------------------------------------- */

/** One row: what it is, when it was saved, and a way to forget it that asks first. */
function pinRow(pin: SavedPin, onForget: () => void): HTMLElement {
  const forget = el(
    'button',
    { class: 'places__forget', type: 'button', 'aria-label': `Forget ${pin.label}` },
    'Forget',
  );

  // A second tap, not a dialog. Losing the car to a mis-tap is exactly what the single-slot
  // store used to do silently, and a confirm step is the cheapest possible cure.
  let armed = false;
  forget.addEventListener('click', (event) => {
    event.stopPropagation();
    if (!armed) {
      armed = true;
      forget.textContent = 'Sure?';
      forget.classList.add('places__forget--armed');
      window.setTimeout(() => {
        armed = false;
        forget.textContent = 'Forget';
        forget.classList.remove('places__forget--armed');
      }, 3000);
      return;
    }
    onForget();
  });

  const open = el(
    'button',
    { class: 'places__open', type: 'button' },
    pin.photo ? el('img', { class: 'places__thumb', src: pin.photo, alt: '' }) : null,
    el(
      'span',
      { class: 'places__text' },
      el('span', { class: 'places__label', text: pin.label }),
      el('span', { class: 'places__age', text: describeAge(pin.pinnedAt) }),
    ),
  );
  open.addEventListener('click', () => navigate(`/pin/${pin.id}`));

  return el('li', { class: 'places__row' }, open, forget);
}

export const placesView: View = (container) => {
  const render = (): void => {
    const pins = listPins();

    if (pins.length === 0) {
      container.replaceChildren(
        el(
          'main',
          { class: 'screen screen--centered solo' },
          markLogo(52),
          el('h1', { class: 'screen__title', text: 'No places yet' }),
          el('p', {
            class: 'screen__body',
            text: 'Save the car, the tent, the umbrella — anything you will want an arrow back to. They stay on this phone.',
          }),
          el(
            'div',
            { class: 'screen__actions' },
            el(
              'button',
              {
                class: 'button button--primary button--block',
                type: 'button',
                onclick: () => navigate('/place?for=pin'),
              },
              'Save a place',
            ),
            el(
              'button',
              {
                class: 'button button--ghost button--block',
                type: 'button',
                onclick: () => navigate('/'),
              },
              'Back',
            ),
          ),
        ),
      );
      return;
    }

    container.replaceChildren(
      el(
        'main',
        { class: 'screen solo' },
        el('h1', { class: 'screen__title', text: 'Your places' }),
        el('p', { class: 'screen__body', text: 'On this phone only. Tap one for an arrow to it.' }),
        el(
          'ul',
          { class: 'places' },
          ...pins.map((pin) =>
            pinRow(pin, () => {
              removePin(pin.id);
              render();
            }),
          ),
        ),
        el(
          'div',
          { class: 'screen__actions' },
          el(
            'button',
            {
              class: 'button button--primary button--block',
              type: 'button',
              onclick: () => navigate('/place?for=pin'),
            },
            'Save another place',
          ),
          el(
            'button',
            {
              class: 'button button--ghost button--block',
              type: 'button',
              onclick: () => navigate('/'),
            },
            'Back',
          ),
        ),
      ),
    );
  };

  render();
};

/* -------------------------------------------------------------------------- */
/* /pin/:id — walking back to one                                             */
/* -------------------------------------------------------------------------- */

export const pinGuideView: View = (container, context) => {
  const id = context.params['id'] ?? '';
  const pin = getPin(id);

  if (!pin) {
    container.replaceChildren(
      errorScreen({
        title: 'That place is gone',
        body: 'It was forgotten, or it was saved on a different phone — saved places never leave the device that made them.',
        actionLabel: 'Your places',
        onAction: () => navigate('/pin'),
        secondaryLabel: 'Home',
        onSecondary: () => navigate('/'),
      }),
    );
    return;
  }

  return runSolo(container, 'pin', pin, {
    onArrived: () =>
      arrivedScreen({
        title: `You are back at ${pin.label.toLowerCase()}`,
        body: describeAge(pin.pinnedAt),
        onDone: () => navigate('/'),
        onClear: () => {
          removePin(pin.id);
          navigate('/pin');
        },
      }),
  });
};
